import type { ValidationRule } from 'ant-design-vue/lib/form/Form';
import type { RuleObject } from 'ant-design-vue/lib/form/interface';
import { ref, computed, unref, Ref, reactive } from 'vue';
import { useI18n } from '@mcex/locale';
import { PASSWORD_REG } from '/@/utils/regExps';

// 访问登录页类型
export enum visitLoginTypeEnum {
  // 通过弹窗
  BY_MODAL,
  // 通过浏览器
  BY_WINDOW,
}
export enum LoginStateEnum {
  CHECK_ACCOUNT_BEFORE_LOGIN, // 检测账号是否存在，且返回loginId列表
  LOGIN,
  REGISTER,
  RESET_PASSWORD,
  MOBILE,
  QR_CODE,
  FORCE_RESET_PASSWORD, // 强制修改密码
}
// 重置密码
export enum ForgetPassStateEnum {
  CHECK_EMAIL,
  RESET_PASSWORD,
  SUCCESS,
}
const currentVisitType = ref(visitLoginTypeEnum.BY_WINDOW);

const currentStep = ref(ForgetPassStateEnum.CHECK_EMAIL);

const currentState = ref(LoginStateEnum.CHECK_ACCOUNT_BEFORE_LOGIN);

// 忘记密码
const forgetFormData = reactive({
  email: '',
  otp: '',
});

export function useVisitLoginType() {
  function setVisitLoginType(state: visitLoginTypeEnum) {
    currentVisitType.value = state;
  }

  const getVisitLoginType = computed(() => currentVisitType.value);

  return { setVisitLoginType, getVisitLoginType };
}

export function useLoginState() {
  function setLoginState(state: LoginStateEnum) {
    currentState.value = state;
  }

  const getLoginState = computed(() => currentState.value);

  function handleBackLogin() {
    setLoginState(LoginStateEnum.CHECK_ACCOUNT_BEFORE_LOGIN);
    currentStep.value = ForgetPassStateEnum.CHECK_EMAIL;
  }

  return { setLoginState, getLoginState, handleBackLogin };
}

export function useFormValid<T extends Object = any>(formRef: Ref<any>) {
  async function validForm() {
    const form = unref(formRef);
    if (!form) return;
    let data: T | null = null;
    try {
      data = await form.validate();
    } catch (error) {
      // console.error('validate error', error);
    }

    return data as T;
  }
  async function validateFields(nameList?: string[]) {
    const form = unref(formRef);
    if (!form) return;
    const data = await form.validateFields(nameList);
    return data as T;
  }
  return { validForm, validateFields };
}

export function useFormRules(formData?: Recordable) {
  const { t } = useI18n();

  const getFirstNameFormRule = computed(() =>
    createRule(t('sys.login.firstNamePlaceholder'), 'blur'),
  );
  const getLastNameFormRule = computed(() =>
    createRule(t('sys.login.lastNamePlaceholder'), 'blur'),
  );
  const getPasswordFormRule = computed(() => createRule(t('sys.login.passwordPlaceholder')));
  const getSmsFormRule = computed(() => createRule(t('sys.login.smsPlaceholder')));
  const getMobileFormRule = computed(() => createRule(t('sys.login.mobilePlaceholder')));

  const validateLoginId = async (_: RuleObject, value: string) => {
    if (!value) {
      return Promise.reject(t('sys.login.loginIdPlaceholder'));
    }
    return Promise.resolve();
  };

  const validateEmail = async (_: RuleObject, value: string) => {
    if (!value) {
      return Promise.reject(t('sys.login.emailPlaceholder'));
    }
    if (value.indexOf('@') === -1 || value.includes(' ')) {
      return Promise.reject(t('sys.login.emailFormatError'));
    }
    return Promise.resolve();
  };
  const validatePolicy = async (_: RuleObject, value: boolean) => {
    return !value ? Promise.reject(t('sys.login.policyPlaceholder')) : Promise.resolve();
  };

  const validatePassword = async (_: RuleObject, value: string) => {
    if (!value) {
      return Promise.reject(t('sys.login.passwordPlaceholder'));
    }
    if (value.length < 6 || value.length > 20) {
      return Promise.reject(t('sys.login.password'));
    }
    // 6-20位字母/数字/符号至少两种组合正则
    if (!PASSWORD_REG.test(value)) {
      return Promise.reject(t('sys.login.passwordError'));
    }
    //校验中文
    const reg = /[\u4E00-\u9FA5]|[\uFE30-\uFFA0]/g;
    if (reg.test(value)) {
      return Promise.reject(t('sys.login.passwordError'));
    }
    //校验空格
    if (/\s/g.test(value)) {
      return Promise.reject(t('sys.login.passwordError'));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = (password: string) => {
    return async (_: RuleObject, value: string) => {
      if (!value) {
        return Promise.reject(t('sys.login.passwordPlaceholder'));
      }
      if (value !== password) {
        return Promise.reject(t('sys.login.diffPwd'));
      }
      return Promise.resolve();
    };
  };
  const validateOtp = async (_: RuleObject, value: string) => {
    if (!value) {
      return Promise.reject(t('sys.login.smsPlaceholder'));
    }
    // 6位
    if (value.length !== 6) {
      return Promise.reject(t('sys.login.smsPlaceholder'));
    }
    return Promise.resolve();
  };
  const getFormRules = computed((): { [k: string]: ValidationRule | ValidationRule[] } => {
    const firstNameFormRule = unref(getFirstNameFormRule) as RuleObject[];
    const lastNameFormRule = unref(getLastNameFormRule) as RuleObject[];
    const passwordFormRule = unref(getPasswordFormRule) as RuleObject[];
    const smsFormRule = unref(getSmsFormRule) as RuleObject[];
    const mobileFormRule = unref(getMobileFormRule) as RuleObject[];

    const mobileRule = {
      sms: smsFormRule,
      mobile: mobileFormRule,
    };
    switch (unref(currentState)) {
      // check account from rules
      case LoginStateEnum.CHECK_ACCOUNT_BEFORE_LOGIN:
        return {
          usernameOrEmail: [
            {
              required: true,
              message: t('sys.login.loginIdPlaceholder'),
              trigger: 'blur',
            },
          ],
        };
      // register form rules
      case LoginStateEnum.REGISTER:
        return {
          email: [{ validator: validateEmail, trigger: 'blur' }],
          firstName: firstNameFormRule,
          lastName: lastNameFormRule,
          password: [{ validator: validatePassword }],
          confirmPassword: [{ validator: validateConfirmPassword(formData?.password) }],
          policy: [{ validator: validatePolicy, trigger: 'change' }],
          otp: [{ required: true, trigger: 'blur', message: t('sys.login.smsPlaceholder') }],
          ...mobileRule,
        };

      // reset password form rules
      case LoginStateEnum.RESET_PASSWORD:
        return {
          email: [{ validator: validateEmail, trigger: 'blur' }],
          //...mobileRule,
          password: [{ validator: validatePassword, trigger: 'change' }],
          confirmPassword: [
            { validator: validateConfirmPassword(formData?.password), trigger: 'change' },
          ],
          otp: [{ validator: validateOtp, trigger: 'change' }],
        };

      // mobile form rules
      case LoginStateEnum.MOBILE:
        return mobileRule;

      // login form rules
      default:
        return {
          email: [{ validator: validateLoginId, trigger: 'blur' }],
          password: passwordFormRule,
        };
    }
  });
  return { getFormRules };
}

function createRule(message: string, trigger = 'change') {
  return [
    {
      required: true,
      message,
      trigger: trigger,
    },
  ];
}

export function useForgetPassState() {
  function setForgetPassState(state: ForgetPassStateEnum) {
    currentStep.value = state;
  }

  const getForgetPassState = computed(() => currentStep.value);

  return { setForgetPassState, getForgetPassState, forgetFormData };
}
